import { useEffect, useState, type ReactNode } from 'react';
import { GitBranch, ChevronRight, Boxes } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from './PageLoader';
import { InlineError } from './InlineError';
import { fieldType } from '../lib/knowledge';
import type { KnowledgeSchema, KnowledgeLabel, KnowledgeRelationshipType, KnowledgeFieldSchema } from '../lib/types';

type Shape = {
    properties?: Record<string, KnowledgeFieldSchema>;
    required?: string[];
    anyOf?: { required: string[] }[];
};

function PropertyList({ shape }: { shape: Shape }) {
    const props = shape.properties ?? {};
    const required = new Set(shape.required ?? []);
    const anyOf = (shape.anyOf ?? []).map(a => a.required);

    if (Object.keys(props).length === 0) {
        return <div className="px-4 py-3 text-[13px] text-coolslate">No properties.</div>;
    }
    return (
        <>
            <div className="divide-y divide-softgrey">
                {Object.entries(props).map(([name, schema]) => (
                    <div key={name} className="px-4 py-2.5 flex items-start gap-3">
                        <div className="w-44 shrink-0">
                            <span className="text-[13px] font-medium text-graphite">{name}</span>
                            {required.has(name) && (
                                <span className="ml-1.5 text-[10px] font-medium text-state-error">required</span>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <code className="text-[11px] text-brand-dark">{fieldType(schema)}</code>
                            {schema.description && (
                                <p className="text-[12px] text-coolslate mt-0.5">{schema.description}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {anyOf.length > 0 && (
                <div className="px-4 py-2 bg-offwhite/40 border-t border-softgrey">
                    <p className="text-[11px] text-coolslate">
                        Requires at least one of:{' '}
                        {anyOf.map(group => group.join(' + ')).join('  |  ')}
                    </p>
                </div>
            )}
        </>
    );
}

function ExpandableCard({ header, description, fieldCount, children }: {
    header: ReactNode;
    description?: string;
    fieldCount: number;
    children: ReactNode;
}) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-softgrey rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-offwhite/50 transition-colors"
                aria-expanded={open}
            >
                <ChevronRight className={`w-4 h-4 text-coolslate shrink-0 mt-0.5 transition-transform ${open ? 'rotate-90' : ''}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">{header}</div>
                    {description && <p className="text-[13px] text-coolslate mt-1">{description}</p>}
                </div>
                <span className="text-[11px] text-coolslate/70 shrink-0 mt-0.5 tabular-nums">
                    {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                </span>
            </button>
            {open && <div className="border-t border-softgrey">{children}</div>}
        </div>
    );
}

const asList = (v: string | string[]) => (Array.isArray(v) ? v : [v]);

function RelationsList({ label, relationships }: { label: string; relationships: KnowledgeRelationshipType[] }) {
    const outgoing = relationships.filter(r => asList(r.from).includes(label));
    const incoming = relationships.filter(r => asList(r.to).includes(label));
    if (outgoing.length === 0 && incoming.length === 0) return null;
    return (
        <div className="px-4 py-3 bg-offwhite/40 border-t border-softgrey grid sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
                <p className="text-[11px] font-semibold text-coolslate uppercase tracking-wide mb-1.5">Outgoing</p>
                {outgoing.length === 0
                    ? <p className="text-[12px] text-coolslate/60">None</p>
                    : <div className="space-y-1">{outgoing.map(r => (
                        <div key={r.type} className="text-[12px]">
                            <code className="text-brand-dark">{r.type}</code>
                            <span className="text-coolslate"> &rarr; {asList(r.to).join(' | ')}</span>
                        </div>
                    ))}</div>}
            </div>
            <div>
                <p className="text-[11px] font-semibold text-coolslate uppercase tracking-wide mb-1.5">Incoming</p>
                {incoming.length === 0
                    ? <p className="text-[12px] text-coolslate/60">None</p>
                    : <div className="space-y-1">{incoming.map(r => (
                        <div key={r.type} className="text-[12px]">
                            <span className="text-coolslate">{asList(r.from).join(' | ')} &rarr; </span>
                            <code className="text-brand-dark">{r.type}</code>
                        </div>
                    ))}</div>}
            </div>
        </div>
    );
}

function LabelCard({ def, relationships }: {
    def: KnowledgeLabel;
    relationships: KnowledgeRelationshipType[];
}) {
    // Shapes arrive flattened from the API (allOf/$defs merged server-side).
    const shape = def.shape;
    return (
        <ExpandableCard
            header={<>
                <span className="text-sm font-semibold text-graphite">{def.name}</span>
                <code className="text-[11px] text-coolslate">{def.label}</code>
            </>}
            description={def.description}
            fieldCount={Object.keys(shape.properties ?? {}).length}
        >
            <PropertyList shape={shape} />
            <RelationsList label={def.label} relationships={relationships} />
        </ExpandableCard>
    );
}

function RelationshipCard({ rel }: { rel: KnowledgeRelationshipType }) {
    const shape: Shape = rel.properties ?? {};
    return (
        <ExpandableCard
            header={<>
                <span className="text-sm font-semibold text-graphite">{rel.type}</span>
                <code className="text-[11px] text-brand-dark">
                    {asList(rel.from).join(' | ')} &rarr; {asList(rel.to).join(' | ')}
                </code>
            </>}
            description={rel.description}
            fieldCount={Object.keys(shape.properties ?? {}).length}
        >
            <PropertyList shape={shape} />
        </ExpandableCard>
    );
}

// What the record can hold: every kind of record and relationship, with the
// properties each carries. Shown as the Library tab of the Knowledge page.
export default function KnowledgeLibrary() {
    const { authFetch } = useAuth();
    const [schema, setSchema] = useState<KnowledgeSchema | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let stale = false;
        authFetch('/knowledge/schema')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load the knowledge library')))
            .then((data: KnowledgeSchema) => { if (!stale) setSchema(data); })
            .catch(err => { if (!stale) { setError(err.message); setSchema({ labels: [], relationships: [], $defs: {} }); } });
        return () => { stale = true; };
    }, [authFetch]);

    if (schema === null) return <PageLoader label="Loading the knowledge library..." />;

    return (
        <div>
            {error && <InlineError message={error} />}

            <p className="text-sm text-coolslate mb-5">
                Everything the record can hold - the kinds of record every connected source maps
                into, and the relationships between them.
            </p>

            <section className="mb-8">
                <h4 className="flex items-center gap-2 navaris-h3 mb-3">
                    <Boxes className="w-4 h-4 text-coolslate" /> Entities
                </h4>
                <div className="space-y-2.5">
                    {[...schema.labels].sort((a, b) => a.name.localeCompare(b.name)).map(def => (
                        <LabelCard key={def.label} def={def} relationships={schema.relationships} />
                    ))}
                </div>
            </section>

            <section>
                <h4 className="flex items-center gap-2 navaris-h3 mb-3">
                    <GitBranch className="w-4 h-4 text-coolslate" /> Relationships
                </h4>
                <div className="space-y-2.5">
                    {[...schema.relationships].sort((a, b) => a.type.localeCompare(b.type)).map(rel => (
                        <RelationshipCard key={rel.type} rel={rel} />
                    ))}
                </div>
            </section>
        </div>
    );
}
